import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { membershipApplicationAPI } from '../../services/api';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  color: #333;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: #555;
`;

const Button = styled.button`
  background: ${props => props.variant === 'secondary' ? '#6c757d' : props.theme.colors.primary};
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  margin-right: 1rem;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const FileInput = styled.input`
  padding: 0.5rem;
  border: 2px dashed #ddd;
  border-radius: 4px;
  width: 100%;
  margin-bottom: 1rem;
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 100%;
  margin-bottom: 1rem;
  font-size: 1rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  
  th, td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid #ddd;
  }
  
  th {
    background: #f8f9fa;
    font-weight: 600;
  }
  
  tr:hover {
    background: #f8f9fa;
  }
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  background: ${props => props.type === 'success' ? '#d4edda' : props.type === 'error' ? '#f8d7da' : '#d1ecf1'};
  color: ${props => props.type === 'success' ? '#155724' : props.type === 'error' ? '#721c24' : '#0c5460'};
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : props.type === 'error' ? '#f5c6cb' : '#bee5eb'};
`;

const ErrorList = styled.ul`
  margin: 0;
  padding-left: 1.5rem;
`;

const MembershipApplicationImport = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [loadingForms, setLoadingForms] = useState(true);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    setLoadingForms(true);
    try {
      const response = await membershipApplicationAPI.getForms();
      const formsList = response.data || [];
      setForms(formsList);
      if (formsList.length > 0 && !selectedFormId) {
        setSelectedFormId(formsList[0].id.toString());
      }
    } catch (error) {
      toast.error('Failed to load forms');
      console.error(error);
    } finally {
      setLoadingForms(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validExtensions = ['.csv', '.xlsx', '.xls'];
      const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
      
      if (!validExtensions.includes(fileExtension)) {
        toast.error('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
        return;
      }
      setFile(selectedFile);
      setPreview(null);
      setResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch('http://localhost:8000/api/membership-applications/import/template', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'membership_application_import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
      console.error(error);
    }
  };

  const handlePreview = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await membershipApplicationAPI.previewImport(formData);
      setPreview(response.data);
      toast.success(`Preview loaded: ${response.data.total_rows} rows found`);
    } catch (error) {
      toast.error('Failed to preview file');
      console.error(error);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    if (!selectedFormId) {
      toast.error('Please select a form first');
      return;
    }

    if (!window.confirm('Are you sure you want to import these records?')) {
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('form_id', selectedFormId);
      
      console.log('📤 Uploading file:', {
        fileName: file.name,
        fileSize: file.size,
        formId: selectedFormId
      });

      const response = await membershipApplicationAPI.importApplications(formData);
      console.log('✅ Import response:', response.data);
      setResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(response.data.message);
      } else {
        toast.warning('No records were imported');
      }
      
      setFile(null);
      setPreview(null);
    } catch (error) {
      console.error('Import error:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to import file';
      const validationErrors = error.response?.data?.errors;
      
      toast.error(errorMessage);
      
      setResult({
        imported: 0,
        failed: 1,
        errors: validationErrors 
          ? Object.entries(validationErrors).map(([field, messages]) => ({
              row: 0,
              errors: Array.isArray(messages) ? messages : [messages],
              data: field
            }))
          : [{ row: 0, errors: [errorMessage], data: '' }]
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Container>
      <Title>Import Membership Applications</Title>

      <Card>
        <Section>
          <SectionTitle>Step 1: Download Template</SectionTitle>
          <p>Download the CSV template with sample data to see the required format.</p>
          <Button onClick={handleDownloadTemplate}>
            📥 Download Template
          </Button>
        </Section>

        <Section>
          <SectionTitle>Step 2: Select Form</SectionTitle>
          <p>Select the membership application form to import data into.</p>
          <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
            <strong>Note:</strong> You can add a "remark" column to your CSV/Excel file:
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
              <li><strong>remark = "approved"</strong>: The submission will be automatically approved and the user will become a member immediately</li>
              <li><strong>remark = "Reserved"</strong>: The submission will have "submitted" status</li>
              <li><strong>No remark or other values</strong>: Defaults to "submitted" status</li>
            </ul>
          </p>
          <Select 
            value={selectedFormId} 
            onChange={(e) => setSelectedFormId(e.target.value)}
            disabled={loadingForms}
          >
            {loadingForms ? (
              <option>Loading forms...</option>
            ) : forms.length === 0 ? (
              <option>No forms available</option>
            ) : (
              forms.map(form => (
                <option key={form.id} value={form.id}>
                  {form.title || form.name} {form.is_active ? '' : '(Inactive)'}
                </option>
              ))
            )}
          </Select>
        </Section>

        <Section>
          <SectionTitle>Step 3: Upload File</SectionTitle>
          <p>Upload your filled CSV or Excel file (.csv, .xlsx, .xls). Make sure all required fields are included.</p>
          <FileInput
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileChange}
          />
          {file && (
            <Alert type="info">
              Selected file: {file.name} ({(file.size / 1024).toFixed(2)} KB)
            </Alert>
          )}
        </Section>

        <Section>
          <SectionTitle>Step 4: Preview and Import</SectionTitle>
          <Button onClick={handlePreview} disabled={!file}>
            👁️ Preview Data
          </Button>
          <Button onClick={handleImport} disabled={!file || importing}>
            {importing ? '⏳ Importing...' : '📤 Import Data'}
          </Button>
        </Section>
      </Card>

      {preview && (
        <Card>
          <SectionTitle>Preview ({preview.total_rows} total rows)</SectionTitle>
          <p>Showing first {preview.preview.length} rows:</p>
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  {preview.headers.map((header, idx) => (
                    <th key={idx}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.preview.map((row, idx) => (
                  <tr key={idx}>
                    {preview.headers.map((header, colIdx) => (
                      <td key={colIdx} style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row[header]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card>
      )}

      {result && (
        <Card>
          <SectionTitle>Import Results</SectionTitle>
          {result.imported > 0 && (
            <Alert type="success">
              ✓ Successfully imported {result.imported} record(s)
            </Alert>
          )}
          {result.failed > 0 && (
            <Alert type="error">
              ✗ Failed to import {result.failed} record(s)
              <ErrorList>
                {result.errors.map((error, idx) => (
                  <li key={idx}>
                    Row {error.row} ({error.data}): {error.errors.join(', ')}
                  </li>
                ))}
              </ErrorList>
            </Alert>
          )}
        </Card>
      )}
    </Container>
  );
};

export default MembershipApplicationImport;

